import { useId, useState, type FormEvent } from 'react';
import { ChevronDown, Loader2, Mic, MicOff } from 'lucide-react';
import { Button, Input, Label } from '@clarix/ui';
import { cn } from '@clarix/ui/lib/cn';
import { QM_LIMITS } from './constants';
import { QM_COPY } from './copy';
import { micDeniedSteps } from './format';
import { MicNotice } from './MicNotice';
import { MicPicker } from './MicPicker';
import type { QuickMeetingMicValue } from './useQuickMeetingMic';
import type { StartInput, LocalPhase } from './useQuickMeetingRecorder';

interface StartRecordingFormProps {
  phase: LocalPhase;
  error: string | null;
  minutesLeft: number | null;
  mic: QuickMeetingMicValue;
  onStart: (input: StartInput) => void;
}

/**
 * The start popover body: optional title, the recording notice, and Start. Pressing Start is the
 * recorder's confirmation that everyone present was told (stamped on the row as the attestation, §6.2).
 */
export function StartRecordingForm({ phase, error, minutesLeft, mic, onStart }: StartRecordingFormProps) {
  const ids = useId();
  const [title, setTitle] = useState('');
  // Collapsed by default; the one-line notice above stays visible and the details are a click away.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const busy = phase === 'requesting_mic' || phase === 'starting';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onStart({ title });
  };

  return (
    <form className="flex flex-col gap-3" onSubmit={submit} noValidate>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${ids}-title`} className="text-md">
          {QM_COPY.titleLabel}
        </Label>
        <Input
          id={`${ids}-title`}
          value={title}
          maxLength={QM_LIMITS.TITLE_MAX}
          placeholder={QM_COPY.titlePlaceholder}
          onChange={(e) => setTitle(e.target.value)}
          disabled={busy}
          className="h-8 text-lg"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <p className="text-md text-muted-foreground">{QM_COPY.notice}</p>
        <button
          type="button"
          className="text-md text-primary focus-visible:ring-ring flex items-center gap-1 self-start rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2"
          aria-expanded={detailsOpen}
          aria-controls={`${ids}-notice`}
          onClick={() => setDetailsOpen((v) => !v)}
        >
          {QM_COPY.noticeDetailsToggle}
          <ChevronDown className={cn('h-3 w-3 transition-transform', detailsOpen && 'rotate-180')} aria-hidden />
        </button>
        {detailsOpen && (
          <ul id={`${ids}-notice`} className="text-md text-muted-foreground list-disc space-y-1 pl-4">
            {QM_COPY.noticeDetails.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
      </div>

      {phase === 'denied' ? (
        <MicNotice icon={MicOff} title={QM_COPY.micDeniedTitle} body={micDeniedSteps()} role="alert" />
      ) : (
        <MicPicker mic={mic} disabled={busy} />
      )}
      {phase === 'requesting_mic' && (
        <p className="text-md text-muted-foreground" role="status">
          {QM_COPY.askingMic}
        </p>
      )}
      {error && (
        <p className="text-md text-destructive-fg" role="alert">
          {error}
        </p>
      )}

      <Button type="submit" size="sm" className="w-full" disabled={busy}>
        {busy ? <Loader2 className="animate-spin" aria-hidden /> : <Mic aria-hidden />}
        {phase === 'starting' ? QM_COPY.starting : phase === 'denied' ? QM_COPY.tryAgain : QM_COPY.start}
      </Button>
      {minutesLeft != null && (
        <p className="text-sm text-muted-foreground text-center">{QM_COPY.minutesLeft(minutesLeft)}</p>
      )}
    </form>
  );
}
